/**
 * flat-svg — the Design Studio's drawing grammar as PURE string builders.
 *
 * Single source of truth for the garment flat, the element glyphs, and the
 * tech-pack callout layout. Three consumers render these strings:
 * the studio canvas and the tech-pack screen (via the `@flint/domain/garment/designs/flat-svg`
 * subpath — this module is dependency-free so the SPA bundles only geometry),
 * and `printing/render-tech-pack-pdf.ts` (Gotenberg HTML).
 *
 * Colours are CSS custom properties with literal fallbacks so the same
 * string renders themed in the app and standalone in the PDF:
 * `--flat-outline` (#334155), `--flat-stitch` (#94a3b8), `--flat-fill` (#fff).
 *
 * ViewBox is 400 × 500; ADR-0029 percent coordinates map as (x×4, y×5).
 */
import { garmentTypeTemplate } from "../styles/templates.js";

// ── Category silhouette imports ──────────────────────────────────────
import { knit_teeFlat, poloFlat, henleyFlat, tank_topFlat, crop_topFlat, sweatshirtFlat, rugby_shirtFlat, active_teeFlat } from "./flat-svg/tops.js";
import { woven_shirtFlat, oxford_shirtFlat, flannel_shirtFlat, blouseFlat, tunicFlat } from "./flat-svg/shirts.js";
import { trouserFlat, jeansFlat, shortsFlat, cargo_pantsFlat, joggersFlat, track_pantsFlat, leggingsFlat, skirtFlat, culottesFlat, cycling_shortsFlat, boxerFlat, briefFlat } from "./flat-svg/bottoms.js";
import { dressFlat, jumpsuitFlat, romperFlat, gownFlat, playsuitFlat } from "./flat-svg/dresses.js";
import { hoodieFlat, jacketFlat, blazerFlat, coatFlat, parkaFlat, bomberFlat, denim_jacketFlat, windcheaterFlat, gilet_vestFlat } from "./flat-svg/outerwear.js";
import { sweaterFlat, cardiganFlat, pulloverFlat } from "./flat-svg/knitwear.js";
import { sports_braFlat, tracksuitFlat } from "./flat-svg/activewear.js";
import { camisoleFlat, pyjama_setFlat, nightwearFlat, robeFlat, thermal_setFlat } from "./flat-svg/loungewear.js";
import { kurtaFlat, kurtiFlat, saree_blouseFlat, lehengaFlat, sherwaniFlat, nehru_jacketFlat, salwarFlat, churidarFlat } from "./flat-svg/ethnicwear.js";
import { kidswearFlat, kids_romperFlat, frockFlat } from "./flat-svg/kidswear.js";
import { capFlat, beanieFlat, scarfFlat, socksFlat, apronFlat, tote_bagFlat } from "./flat-svg/accessories.js";

export type FlatView = "front" | "back";

export const FLAT_ELEMENT_TYPES = [
  "logo_print",
  "embroidery_stone",
  "button",
  "pocket",
  "placket",
  "collar",
  "cuff",
  "stitch",
  "label",
  "wash_care",
  "text",
  "trim",
  "packing",
] as const;
export type FlatElementType = (typeof FLAT_ELEMENT_TYPES)[number];

const OUTLINE = "var(--flat-outline, #334155)";
const STITCH = "var(--flat-stitch, #94a3b8)";
const FILL = "var(--flat-fill, #ffffff)";
const INK = "var(--flat-ink, #0f172a)";
const PAPER = "var(--flat-fill, #ffffff)";

/**
 * Dispatcher: maps every garment type to its dedicated silhouette function.
 * `categoryFallback()` remains as safety net for truly unknown types only.
 */
const SILHOUETTE_MAP: Record<string, (view: FlatView) => string> = {
  // ── Knit tops ──
  knit_tee: knit_teeFlat,
  polo: poloFlat,
  henley: henleyFlat,
  tank_top: tank_topFlat,
  crop_top: crop_topFlat,
  sweatshirt: sweatshirtFlat,
  rugby_shirt: rugby_shirtFlat,
  // ── Woven tops ──
  woven_shirt: woven_shirtFlat,
  oxford_shirt: oxford_shirtFlat,
  flannel_shirt: flannel_shirtFlat,
  blouse: blouseFlat,
  tunic: tunicFlat,
  // ── Bottoms ──
  trouser: trouserFlat,
  jeans: jeansFlat,
  shorts: shortsFlat,
  cargo_pants: cargo_pantsFlat,
  joggers: joggersFlat,
  track_pants: track_pantsFlat,
  leggings: leggingsFlat,
  skirt: skirtFlat,
  culottes: culottesFlat,
  // ── Dresses & one-piece ──
  dress: dressFlat,
  jumpsuit: jumpsuitFlat,
  romper: romperFlat,
  gown: gownFlat,
  playsuit: playsuitFlat,
  // ── Outerwear ──
  hoodie: hoodieFlat,
  jacket: jacketFlat,
  blazer: blazerFlat,
  coat: coatFlat,
  parka: parkaFlat,
  bomber: bomberFlat,
  denim_jacket: denim_jacketFlat,
  windcheater: windcheaterFlat,
  gilet_vest: gilet_vestFlat,
  // ── Knitwear ──
  sweater: sweaterFlat,
  cardigan: cardiganFlat,
  pullover: pulloverFlat,
  // ── Activewear ──
  sports_bra: sports_braFlat,
  active_tee: active_teeFlat,
  cycling_shorts: cycling_shortsFlat,
  tracksuit: tracksuitFlat,
  // ── Inner & loungewear ──
  boxer: boxerFlat,
  brief: briefFlat,
  camisole: camisoleFlat,
  pyjama_set: pyjama_setFlat,
  nightwear: nightwearFlat,
  robe: robeFlat,
  thermal_set: thermal_setFlat,
  // ── Ethnicwear (India) ──
  kurta: kurtaFlat,
  kurti: kurtiFlat,
  salwar: salwarFlat,
  churidar: churidarFlat,
  saree_blouse: saree_blouseFlat,
  lehenga: lehengaFlat,
  sherwani: sherwaniFlat,
  nehru_jacket: nehru_jacketFlat,
  // ── Kidswear ──
  kidswear: kidswearFlat,
  kids_romper: kids_romperFlat,
  frock: frockFlat,
  // ── Accessories ──
  cap: capFlat,
  beanie: beanieFlat,
  scarf: scarfFlat,
  socks: socksFlat,
  apron: apronFlat,
  tote_bag: tote_bagFlat,
};

/** The flat's inner SVG markup (no `<svg>` wrapper) for a 400×500 viewBox. */
export function garmentFlatSvg(garmentType: string, view: FlatView): string {
  const fn = SILHOUETTE_MAP[garmentType];
  if (fn) return fn(view);
  return categoryFallback(garmentType, view);
}

/**
 * Category-based silhouette fallback for truly unknown types.
 * Resolves category from the template registry, then picks the closest
 * silhouette. Falls back to tee if category is unknown.
 */
function categoryFallback(garmentType: string, view: FlatView): string {
  const tpl = garmentTypeTemplate(garmentType);
  if (!tpl) return knit_teeFlat(view);

  switch (tpl.category) {
    case "knit_tops":
    case "activewear":
    case "inner_loungewear":
      return knit_teeFlat(view);
    case "woven_tops":
    case "ethnicwear":
      return woven_shirtFlat(view);
    case "bottoms":
      return trouserFlat(view);
    case "dresses_onepiece":
      return dressFlat(view);
    case "outerwear":
    case "knitwear":
      return hoodieFlat(view);
    case "kidswear":
      return kidswearFlat(view);
    case "accessories":
    default:
      return knit_teeFlat(view);
  }
}

/* ── Element glyphs ────────────────────────────────────────────────── */

export interface GlyphBox {
  width: number;
  height: number;
}

/** The flat body is ~116 units wide for a ~55 cm chest → 1 mm ≈ 0.21 units. */
export const MM_TO_UNIT = 0.21;

/** Rendered footprint (flat units) — drives hit area + true-to-mm sizing. */
export function glyphBox(
  type: FlatElementType,
  widthMm: number | null,
  heightMm: number | null,
): GlyphBox {
  const w = widthMm ? Math.max(14, widthMm * MM_TO_UNIT) : null;
  const h = heightMm ? Math.max(10, heightMm * MM_TO_UNIT) : null;
  switch (type) {
    case "logo_print":
      return { width: w ?? 42, height: h ?? 30 };
    case "embroidery_stone":
      return { width: w ?? 34, height: h ?? 34 };
    case "button":
      return { width: w ?? 14, height: h ?? 14 };
    case "pocket":
      return { width: w ?? 44, height: h ?? 50 };
    case "placket":
      return { width: w ?? 16, height: h ?? 120 };
    case "collar":
      return { width: w ?? 56, height: h ?? 18 };
    case "cuff":
      return { width: w ?? 40, height: h ?? 16 };
    case "stitch":
      return { width: w ?? 48, height: h ?? 8 };
    case "label":
      return { width: w ?? 28, height: h ?? 16 };
    case "wash_care":
      return { width: w ?? 22, height: h ?? 18 };
    case "text":
      return { width: w ?? 44, height: h ?? 16 };
    case "trim":
      return { width: w ?? 24, height: h ?? 12 };
    case "packing":
      return { width: w ?? 28, height: h ?? 24 };
  }
}

const GLYPH_DETAIL = `stroke="${OUTLINE}" stroke-width="1.4" fill="none" stroke-linejoin="round" stroke-linecap="round"`;
const GLYPH_DETAIL_FILLED = `stroke="${OUTLINE}" stroke-width="1.4" fill="${FILL}" stroke-linejoin="round" stroke-linecap="round"`;
const GLYPH_STITCH = `stroke="${STITCH}" stroke-width="1.1" stroke-dasharray="4 3" fill="none" stroke-linecap="round"`;

/**
 * The element drawn as the actual feature (pocket looks like a pocket),
 * centred on (0,0) — callers wrap in `translate(x, y)`.
 */
export function elementGlyphSvg(type: FlatElementType, box: GlyphBox): string {
  const w = box.width;
  const h = box.height;
  const hw = w / 2;
  const hh = h / 2;

  switch (type) {
    case "logo_print":
      return `<g><rect x="${-hw}" y="${-hh}" width="${w}" height="${h}" rx="2" ${GLYPH_DETAIL} fill="${FILL}" fill-opacity="0.6"/><path ${GLYPH_STITCH} d="M ${-hw} ${-hh} L ${hw} ${hh} M ${hw} ${-hh} L ${-hw} ${hh}"/></g>`;
    case "embroidery_stone": {
      const r = Math.min(hw, hh);
      return `<g><circle r="${r}" ${GLYPH_DETAIL} stroke-dasharray="2.5 2.5" fill="${FILL}" fill-opacity="0.6"/><circle r="${r * 0.45}" ${GLYPH_STITCH}/><circle r="1.6" fill="${OUTLINE}" stroke="none"/></g>`;
    }
    case "button": {
      const r = Math.min(hw, hh);
      const o = r * 0.34;
      const hole = (cx: number, cy: number) =>
        `<circle cx="${cx}" cy="${cy}" r="0.9" fill="${OUTLINE}" stroke="none"/>`;
      return `<g><circle r="${r}" ${GLYPH_DETAIL_FILLED}/>${hole(-o, -o)}${hole(o, -o)}${hole(-o, o)}${hole(o, o)}</g>`;
    }
    case "pocket":
      return `<g><path ${GLYPH_DETAIL_FILLED} d="M ${-hw} ${-hh} L ${hw} ${-hh} L ${hw} ${hh * 0.45} L 0 ${hh} L ${-hw} ${hh * 0.45} Z"/><path ${GLYPH_STITCH} d="M ${-hw + 3.5} ${-hh + 3.5} L ${hw - 3.5} ${-hh + 3.5} L ${hw - 3.5} ${hh * 0.42} L 0 ${hh - 4} L ${-hw + 3.5} ${hh * 0.42} Z"/></g>`;
    case "placket": {
      const dots = [-0.6, 0, 0.6]
        .map((t) => `<circle cy="${t * hh}" r="3.4" ${GLYPH_DETAIL_FILLED}/>`)
        .join("");
      return `<g><path ${GLYPH_DETAIL} d="M ${-hw * 0.5} ${-hh} L ${-hw * 0.5} ${hh} M ${hw * 0.5} ${-hh} L ${hw * 0.5} ${hh}"/><path ${GLYPH_STITCH} d="M ${-hw * 0.5 + 2.5} ${-hh + 2} L ${-hw * 0.5 + 2.5} ${hh - 2}"/>${dots}</g>`;
    }
    case "collar":
      return `<g><path ${GLYPH_DETAIL} d="M ${-hw} ${hh} L 0 ${-hh} L ${hw} ${hh}"/><path ${GLYPH_STITCH} d="M ${-hw * 0.7} ${hh * 0.5} L 0 ${-hh * 0.5} L ${hw * 0.7} ${hh * 0.5}"/></g>`;
    case "cuff":
      return `<g><rect x="${-hw}" y="${-hh}" width="${w}" height="${h}" rx="2" ${GLYPH_DETAIL} fill="${FILL}" fill-opacity="0.6"/><path ${GLYPH_STITCH} d="M ${-hw + 2.5} ${-hh + 3} L ${hw - 2.5} ${-hh + 3} M ${-hw + 2.5} ${hh - 3} L ${hw - 2.5} ${hh - 3}"/></g>`;
    case "stitch":
      return `<path ${GLYPH_STITCH} stroke-width="1.6" d="M ${-hw} 0 L ${hw} 0"/>`;
    case "label":
      return `<g><rect x="${-hw}" y="${-hh}" width="${w}" height="${h}" rx="1.5" ${GLYPH_DETAIL_FILLED}/><path ${GLYPH_STITCH} d="M ${-hw + 3} ${-hh + h * 0.4} L ${hw - 3} ${-hh + h * 0.4}"/></g>`;
    case "wash_care":
      return `<g><rect x="${-hw}" y="${-hh}" width="${w}" height="${h}" rx="1.5" ${GLYPH_DETAIL} stroke-dasharray="3 2.5" fill="${FILL}"/><path ${GLYPH_DETAIL} stroke-width="1" d="M ${-hw * 0.5} ${hh * 0.25} Q 0 ${-hh * 0.35} ${hw * 0.5} ${hh * 0.25}"/></g>`;
    case "text":
      return `<g><rect x="${-hw}" y="${-hh}" width="${w}" height="${h}" rx="1.5" ${GLYPH_STITCH} fill="${FILL}" fill-opacity="0.5"/><text y="${hh * 0.42}" text-anchor="middle" font-size="${Math.min(h * 0.72, 11)}" fill="${OUTLINE}" font-weight="600">Aa</text></g>`;
    case "trim":
      return `<g><path ${GLYPH_DETAIL_FILLED} d="M ${-hw} ${hh} L ${hw} ${hh} L 0 ${-hh} Z"/><path ${GLYPH_STITCH} d="M ${-hw * 0.5} ${hh} L 0 ${-hh * 0.2} L ${hw * 0.5} ${hh}"/></g>`;
    case "packing":
      return `<g><rect x="${-hw}" y="${-hh * 0.55}" width="${w}" height="${h * 0.78}" ${GLYPH_DETAIL_FILLED}/><path ${GLYPH_DETAIL} d="M ${-hw} ${-hh * 0.55} L 0 ${-hh} L ${hw} ${-hh * 0.55}"/><path ${GLYPH_STITCH} d="M 0 ${-hh * 0.55} L 0 ${hh * 0.23}"/></g>`;
  }
}

/* ── Tech-pack callout layout ──────────────────────────────────────── */

export interface CalloutElement {
  id: string;
  elementType: string;
  x: number;
  y: number;
  widthMm: number | null;
  heightMm: number | null;
  offsetNote: string | null;
}

/** Min vertical gap between callout labels, in canvas % of height. */
const SLOT_GAP = 10;

/**
 * Assign each element a label slot on its side of the margin so leader
 * lines NEVER cross: sort by target y, start at the target's height, push
 * down to keep the gap, then push back up if the stack overran. Sorted
 * labels + sorted targets + elbow leaders ⇒ planar by construction.
 */
export function calloutSlots(elements: readonly CalloutElement[]): Map<string, number> {
  const sorted = [...elements].sort((a, b) => a.y - b.y);
  const slots = sorted.map((e) => Math.min(92, Math.max(6, e.y)));
  for (let i = 1; i < slots.length; i++) {
    slots[i] = Math.max(slots[i]!, slots[i - 1]! + SLOT_GAP);
  }
  for (let i = slots.length - 1; i >= 0; i--) {
    const ceiling = i === slots.length - 1 ? 94 : slots[i + 1]! - SLOT_GAP;
    slots[i] = Math.min(slots[i]!, ceiling);
  }
  const byId = new Map<string, number>();
  sorted.forEach((e, i) => byId.set(e.id, slots[i]!));
  return byId;
}

const CALLOUT_LABELS: Record<FlatElementType, string> = {
  logo_print: "LOGO / PRINT",
  embroidery_stone: "EMBROIDERY / STONE",
  button: "BUTTON",
  pocket: "POCKET",
  placket: "PLACKET",
  collar: "COLLAR",
  cuff: "CUFF",
  stitch: "STITCH",
  label: "LABEL",
  wash_care: "WASH CARE",
  text: "TEXT",
  trim: "TRIM",
  packing: "PACKING",
};

export function calloutLabel(type: string): string {
  return CALLOUT_LABELS[type as FlatElementType] ?? type.toUpperCase();
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Split a note into ≤`max`-char lines (word-wrapped, max 3 lines). */
function wrapNote(note: string, max = 26): string[] {
  const words = note.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (current === "") current = word;
    else if ((current + " " + word).length <= max) current += " " + word;
    else {
      lines.push(current);
      current = word;
    }
    if (lines.length === 3) break;
  }
  if (lines.length < 3 && current !== "") lines.push(current);
  return lines;
}

function coerceType(type: string): FlatElementType | null {
  return (FLAT_ELEMENT_TYPES as readonly string[]).includes(type)
    ? (type as FlatElementType)
    : null;
}

/**
 * The complete tech-pack figure for one view: flat + drawn glyphs + margin
 * callouts (labels as SVG text) + non-crossing elbow leader lines.
 * Returns a full `<svg>` element string.
 */
export function flatWithCalloutsSvg(input: {
  garmentType: string;
  view: FlatView;
  elements: readonly CalloutElement[];
}): string {
  const left = input.elements.filter((e) => e.x < 50);
  const right = input.elements.filter((e) => e.x >= 50);
  const leftSlots = calloutSlots(left);
  const rightSlots = calloutSlots(right);

  const glyphs = input.elements
    .flatMap((element) => {
      const type = coerceType(element.elementType);
      if (!type) return [];
      const box = glyphBox(type, element.widthMm, element.heightMm);
      return [
        `<g transform="translate(${element.x * 4}, ${element.y * 5})">${elementGlyphSvg(type, box)}</g>`,
      ];
    })
    .join("\n");

  const callout = (element: CalloutElement, side: "left" | "right"): string => {
    const slots = side === "left" ? leftSlots : rightSlots;
    const slotY = (slots.get(element.id) ?? element.y) * 5;
    const labelX = side === "left" ? 6 : 394;
    const channelX = side === "left" ? 92 : 308;
    const tx = element.x * 4;
    const ty = element.y * 5;
    const anchor = side === "left" ? "start" : "end";
    const noteLines = element.offsetNote ? wrapNote(`[${element.offsetNote}]`) : [];
    const noteText = noteLines
      .map(
        (line, i) =>
          `<text x="${labelX}" y="${slotY + 11 + i * 10}" text-anchor="${anchor}" font-size="8.5" fill="${STITCH}">${escapeXml(line)}</text>`,
      )
      .join("");
    return `<g stroke="${INK}" stroke-width="1.1" fill="none">
  <polyline points="${labelX},${slotY} ${channelX},${slotY} ${tx},${ty}"/>
  <circle cx="${tx}" cy="${ty}" r="3.2" fill="${INK}" stroke="${PAPER}" stroke-width="1"/>
</g>
<text x="${labelX}" y="${slotY - 4}" text-anchor="${anchor}" font-size="9.5" font-weight="700" letter-spacing="0.5" fill="${INK}">${escapeXml(calloutLabel(element.elementType))}</text>
${noteText}`;
  };

  const callouts = [
    ...left.map((e) => callout(e, "left")),
    ...right.map((e) => callout(e, "right")),
  ].join("\n");

  return `<svg viewBox="0 0 400 500" xmlns="http://www.w3.org/2000/svg" role="img">
${garmentFlatSvg(input.garmentType, input.view)}
${glyphs}
${callouts}
</svg>`;
}
